/**
 * Plausible Analytics integration for NFA
 *
 * Plausible is a privacy-friendly, cookie-free analytics solution.
 * Events are sent to Plausible's servers (or self-hosted instance).
 *
 * To enable:
 * 1. Set VITE_PLAUSIBLE_DOMAIN in .env (e.g., "notfuckingabout.com")
 * 2. Optionally set VITE_PLAUSIBLE_API_HOST for self-hosted (defaults to "https://plausible.io")
 */

interface PlausibleConfig {
  domain: string;
  apiHost: string;
  trackLocalhost: boolean;
}

interface PlausibleEvent {
  name: string;
  props?: Record<string, string | number | boolean>;
  url?: string;
  referrer?: string;
}

// Plausible event queue before script loads
const eventQueue: PlausibleEvent[] = [];

// Plausible configuration
const config: PlausibleConfig = {
  domain: import.meta.env.VITE_PLAUSIBLE_DOMAIN || '',
  apiHost: import.meta.env.VITE_PLAUSIBLE_API_HOST || 'https://plausible.io',
  trackLocalhost: import.meta.env.DEV && import.meta.env.VITE_PLAUSIBLE_DEBUG === 'true',
};

/**
 * Check if analytics is enabled
 */
export function isAnalyticsEnabled(): boolean {
  if (!config.domain) {
    return false;
  }

  // Don't track on localhost unless explicitly enabled
  if (window.location.hostname === 'localhost' && !config.trackLocalhost) {
    return false;
  }

  return true;
}

/**
 * Initialize Plausible analytics
 * This should be called once on app startup
 */
export function initAnalytics(): void {
  if (!isAnalyticsEnabled()) {
    console.log('[Analytics] Disabled - no domain configured');
    return;
  }

  // Check if script already loaded
  if (document.querySelector(`script[data-domain="${config.domain}"]`)) {
    return;
  }

  // Create and inject the Plausible script
  const script = document.createElement('script');
  script.defer = true;
  script.dataset.domain = config.domain;
  script.dataset.api = `${config.apiHost}/api/event`;
  script.src = `${config.apiHost}/js/script.js`;

  // Process queued events once script loads
  script.onload = () => {
    eventQueue.forEach(event => trackEvent(event.name, event.props));
    eventQueue.length = 0;
  };

  document.head.appendChild(script);

  console.log('[Analytics] Plausible initialized for', config.domain);
}

/**
 * Track a custom event with optional properties
 */
export function trackEvent(
  eventName: string,
  props?: Record<string, string | number | boolean>
): void {
  if (!isAnalyticsEnabled()) {
    console.log('[Analytics] Event (disabled):', eventName, props);
    return;
  }

  // Check if plausible function is available
  const plausible = (window as unknown as { plausible?: (name: string, options?: { props?: Record<string, unknown> }) => void }).plausible;

  if (plausible) {
    plausible(eventName, props ? { props } : undefined);
    console.log('[Analytics] Event tracked:', eventName, props);
  } else {
    // Queue event for later
    eventQueue.push({ name: eventName, props });
    console.log('[Analytics] Event queued:', eventName, props);
  }
}

/**
 * Track page view (automatically handled by Plausible script)
 * Call this for SPA navigation if not using the hash routing extension
 */
export function trackPageView(url?: string): void {
  if (!isAnalyticsEnabled()) {
    return;
  }

  const plausible = (window as unknown as { plausible?: (name: string, options?: { u?: string }) => void }).plausible;

  if (plausible) {
    plausible('pageview', url ? { u: url } : undefined);
  }
}

// Pre-defined event types for the NFA app
export const AnalyticsEvents = {
  // Kit events
  KIT_SEARCH: 'Kit Search',
  KIT_RESULT_VIEW: 'Kit Result View',

  // Product events
  PRODUCT_SWAP: 'Product Swap',
  PRODUCT_MARK_OWNED: 'Product Mark Owned',
  AFFILIATE_CLICK: 'Affiliate Click',

  // Export events
  EXPORT_PDF: 'Export PDF',
  EXPORT_COPY_LIST: 'Export Copy List',
  EXPORT_SHARE: 'Export Share',

  // Route events
  ROUTE_VIEW: 'Route View',

  // Error events
  SEARCH_ERROR: 'Search Error',
} as const;

/**
 * Helper functions for common tracking scenarios
 */

export function trackKitSearch(query: string): void {
  trackEvent(AnalyticsEvents.KIT_SEARCH, {
    query_length: query.length,
  });
}

export function trackKitResult(location: string, itemCount: number, totalPrice: number): void {
  trackEvent(AnalyticsEvents.KIT_RESULT_VIEW, {
    location,
    item_count: itemCount,
    total_price: totalPrice,
  });
}

export function trackProductSwap(categoryId: string, fromTier: string, toTier: string): void {
  trackEvent(AnalyticsEvents.PRODUCT_SWAP, {
    category: categoryId,
    from_tier: fromTier,
    to_tier: toTier,
  });
}

export function trackProductOwned(_productId: string, isOwned: boolean): void {
  trackEvent(AnalyticsEvents.PRODUCT_MARK_OWNED, {
    action: isOwned ? 'mark_owned' : 'unmark_owned',
  });
}

export function trackAffiliateClick(productId: string, retailer: string): void {
  trackEvent(AnalyticsEvents.AFFILIATE_CLICK, {
    product_id: productId,
    retailer,
  });
}

export function trackExport(type: 'pdf' | 'copy' | 'share'): void {
  const eventMap = {
    pdf: AnalyticsEvents.EXPORT_PDF,
    copy: AnalyticsEvents.EXPORT_COPY_LIST,
    share: AnalyticsEvents.EXPORT_SHARE,
  };
  trackEvent(eventMap[type]);
}

export function trackSearchError(errorType: string): void {
  trackEvent(AnalyticsEvents.SEARCH_ERROR, {
    error_type: errorType,
  });
}

export function trackRouteView(routeName: string, country: string): void {
  trackEvent(AnalyticsEvents.ROUTE_VIEW, {
    route_name: routeName,
    country,
  });
}
